import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { RCDropdown } from '@shared/components/RCDropdown';
import { RCMenu } from '@shared/components/RCMenu';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { MBMenuRow } from '@shared/scenes/cards/bulk/components/menuRow';
import { ColorThemeContext, grid } from '@shared/styles';
import { DATE_RANGE_COMPONENT, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';

import { componentTemplateOptions, prepareComponent } from '../../config';
import { useWorkflowDispatch, useWorkflowTemplateState } from '../../context/context';
import { ComponentTemplateSection } from '../../template/ComponentTemplateSection';
import { FieldOption } from '../../template/options/FieldOption';

interface Props {
  isSubform: boolean;
  disableOpenSheet: boolean;
  setShowingSheet: Dispatch<SetStateAction<boolean>>;
  showingSheet: boolean;
}

export function MBWorkflowAddComponent({
  isSubform,
  showingSheet,
  setShowingSheet,
  disableOpenSheet,
}: Props) {
  const colors = useContext(ColorThemeContext);

  return (
    <MBTouchableOpacity disabled={disableOpenSheet} onPress={() => setShowingSheet(!showingSheet)}>
      <View row centerVertically padding={2}>
        <RCDropdown
          trigger={['click']}
          onVisibleChange={setShowingSheet}
          visible={showingSheet}
          overlay={<ComponentOptionsMenu setShowingSheet={setShowingSheet} isSubform={isSubform} />}
        >
          <>
            <MBIconV2 name="plus" color={colors.accent} size={grid(5)} />
            <View paddingLeft={1} />
            <MBTextBody1 color={colors.foregroundInactive}>
              {i18n('workflowTemplate.addField')}
            </MBTextBody1>
          </>
        </RCDropdown>
      </View>
    </MBTouchableOpacity>
  );
}

function ComponentOptionsMenu({
  setShowingSheet,
  isSubform,
}: Pick<Props, 'setShowingSheet' | 'isSubform'>) {
  const dispatch = useWorkflowDispatch();
  const { workflowTemplate } = useWorkflowTemplateState();
  const { components, archivedComponents } = workflowTemplate;
  const isDateRangeComponentEnabled = useFeatureFlag(DATE_RANGE_COMPONENT);

  const allComponentIds = useMemo(() => {
    const allComponents = [...components, ...(archivedComponents ?? [])];
    return allComponents.map(component => component.id);
  }, [components, archivedComponents]);

  const onSelectField = useCallback(
    (option: FieldOption) => {
      try {
        setShowingSheet(false);
        dispatch({
          type: 'addComponent',
          args: prepareComponent(option.component),
        });
        const missingDependencies = option.missingDependencies ?? [];
        missingDependencies.forEach(dep => {
          dispatch({ type: 'addComponent', args: prepareComponent(dep.component) });
        });
      } catch {
        console.error('MBWorkflowAddComponent: Invalid template options');
      }
    },
    [dispatch, setShowingSheet],
  );

  const sectionsToDisplay = useMemo(() => {
    return componentTemplateOptions
      .sectionsToDisplay(isSubform, workflowTemplate.components)
      .map(section => {
        return {
          ...section,
          items: section.items.filter(
            item => isDateRangeComponentEnabled || item.component.type !== 'DATE_RANGE',
          ),
        };
      });
  }, [isSubform, workflowTemplate.components, isDateRangeComponentEnabled]);

  return (
    <View>
      <RCMenu mode="vertical" expandIcon={null} style={{ maxHeight: '95vh', overflow: 'auto' }}>
        {sectionsToDisplay.map((section, index) => (
          <ComponentOptionsMenuSection
            key={index}
            section={section}
            allComponentIds={allComponentIds}
            onSelectField={onSelectField}
            index={index}
          />
        ))}
      </RCMenu>
    </View>
  );
}

function ComponentOptionsMenuSection({
  section,
  allComponentIds,
  onSelectField,
  index,
}: {
  section: ComponentTemplateSection;
  allComponentIds: string[];
  onSelectField: (item: FieldOption) => void;
  index: number;
}) {
  const colors = useContext(ColorThemeContext);
  const { title, items } = section;

  const validItems = useMemo(() => {
    return items.filter(({ component }) => !allComponentIds.includes(component.id as string));
  }, [items, allComponentIds]);

  return (
    <>
      <View paddingTop={index === 0 ? 1 : 2} paddingBottom={1} paddingHorizontal={3}>
        <MBTextOverlineBold color={colors.foregroundInactive}>{title}</MBTextOverlineBold>
      </View>
      {validItems.map(item => (
        <MBMenuRow
          isHidingPressableIndicator
          onPress={() => onSelectField(item)}
          title={item.title}
          key={item.title}
          image={<MBIconV2 name={item.icon} color={colors.foregroundActive} size={grid(3)} />}
          imageStyle={{ marginRight: grid(1) }}
        />
      ))}
    </>
  );
}
